import { MutationTree } from 'vuex';
import { Account, RandomizableAccount } from '../account/types';
import { DomainAccountState } from './types';
import * as types from './mutation-types';
import { ErrorDetail } from '../../types';
import { NodeDomain } from '../node_domain/types';

// 全てのエラーをクリアする
const resetAllErrors = (state: DomainAccountState): void => {
    const searchState = state.search;
    searchState.error = false;
    searchState.errorCode = undefined;
    searchState.errorMsg = undefined;

    const directSearchState = state.directSearch;
    directSearchState.error = false;
    directSearchState.errorCode = undefined;
    directSearchState.errorMsg = undefined;

    const createState = state.create;
    createState.error = false;
    createState.errorTitle = undefined;
    createState.errorDetails = undefined;

    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;

    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;

    const connectionState = state.connection;
    connectionState.error = false;
    connectionState.errorTitle = undefined;
    connectionState.errorDetails = undefined;

    const randomizableAccountState = state.randomizableAccount;
    randomizableAccountState.error = false;
    randomizableAccountState.errorCode = undefined;
    randomizableAccountState.errorMsg = undefined;
};

export const mutations: MutationTree<DomainAccountState> = {
    // ノードドメインアカウント検索系
    [types.SEARCH_START](state) {
        resetAllErrors(state);
        const searchState = state.search;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; domain_accounts: Array<Account> }) {
        state.domainAccountCount = payload.count;
        state.domainAccounts = payload.domain_accounts;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const searchState = state.search;
        searchState.errorCode = payload.code;
        searchState.errorMsg = payload.message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // ノードドメインアカウント検索系（ドメインサーバー内の検索）
    [types.DIRECT_SEARCH_START](state) {
        resetAllErrors(state);
        const directSearchState = state.directSearch;
        directSearchState.isProcessing = true;
        directSearchState.isFetchSuccess = false;
    },

    [types.DIRECT_SEARCH_SUCCESS](state, payload: { domain_accounts: Array<Account> }) {
        state.wkDomainAccountCount = payload.domain_accounts.length;
        state.wkDomainAccounts = payload.domain_accounts;

        const directSearchState = state.directSearch;
        directSearchState.isProcessing = false;
        directSearchState.isFetchSuccess = true;
    },

    [types.DIRECT_SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const directSearchState = state.directSearch;
        directSearchState.errorCode = payload.code;
        directSearchState.errorMsg = payload.message;
        directSearchState.error = true;
        directSearchState.isProcessing = false;
        directSearchState.isFetchSuccess = false;
    },

    // ノードドメインアカウント新規登録
    [types.CREATE_START](state) {
        resetAllErrors(state);
        const createState = state.create;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state) {
        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // ノードドメインアカウント詳細情報参照系
    [types.SHOW_START](state) {
        resetAllErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: { domain_account: Account; domain: NodeDomain }) {
        state.domainAccountDetails.account = payload.domain_account;
        state.domainAccountDetails.domain = payload.domain;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // ノードドメインアカウント削除系
    [types.DESTROY_START](state) {
        resetAllErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // ノードドメインアカウント更新系
    [types.UPDATE_START](state) {
        resetAllErrors(state);
        const updateState = state.update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_SUCCESS](state) {
        const updateState = state.update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // 接続テスト系
    [types.CONNECTION_START](state) {
        resetAllErrors(state);
        const connectionState = state.connection;
        connectionState.isProcessing = true;
    },

    [types.CONNECTION_SUCCESS](state) {
        const connectionState = state.connection;
        connectionState.isProcessing = false;
    },

    [types.CONNECTION_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const connectionState = state.connection;
        connectionState.errorTitle = payload.errorTitle;
        connectionState.errorDetails = payload.errorDetails;
        connectionState.error = true;
        connectionState.isProcessing = false;
    },

    // パスワードランダム化対象のノードドメイン管理者アカウントと登録済みアカウントの取得
    [types.NODE_DOMAIN_RANDOMIZABLE_ACCOUNT_START](state) {
        resetAllErrors(state);
        const randomizableAccountState = state.randomizableAccount;
        randomizableAccountState.isProcessing = true;
        randomizableAccountState.isFetchSuccess = false;
    },

    [types.NODE_DOMAIN_RANDOMIZABLE_ACCOUNT_SUCCESS](
        state,
        payload: { count: number; account_type: string; accounts: Array<RandomizableAccount> }
    ) {
        state.domainRandomizableAccounts = payload.accounts;
        state.domainRandomizableAccountCount = payload.count;
        state.domainRandomizableAccountType = payload.account_type;

        const randomizableAccountState = state.randomizableAccount;
        randomizableAccountState.isProcessing = false;
        randomizableAccountState.isFetchSuccess = true;
    },

    [types.NODE_DOMAIN_RANDOMIZABLE_ACCOUNT_FAILURE](state, payload: { code: string; message: string }) {
        const randomizableAccountState = state.randomizableAccount;
        randomizableAccountState.errorCode = payload.code;
        randomizableAccountState.errorMsg = payload.message;
        randomizableAccountState.error = true;
        randomizableAccountState.isProcessing = false;
        randomizableAccountState.isFetchSuccess = false;
    },

    // ドメインアカウント検索済みデータ削除
    [types.CLEAR_WORK](state) {
        state.wkDomainAccountCount = 0;
        state.wkDomainAccounts = undefined;
    },

    // エラーのみリセットする
    [types.RESET_ERROR](state) {
        resetAllErrors(state);
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const directSearchState = state.directSearch;
        directSearchState.isProcessing = false;
        directSearchState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;

        const updateState = state.update;
        updateState.isProcessing = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const destroyState = state.destroy;
        destroyState.isProcessing = false;

        const connectionState = state.connection;
        connectionState.isProcessing = false;

        const randomizableAccountState = state.randomizableAccount;
        randomizableAccountState.isProcessing = false;
        randomizableAccountState.isFetchSuccess = false;
    },

    // エラーのみリセットする
    [types.CLEAR_ALL](state) {
        resetAllErrors(state);

        state.domainAccounts = undefined;
        state.domainAccountCount = 0;
        state.domainAccountDetails.account = undefined;
        state.domainAccountDetails.domain = undefined;

        state.wkDomainAccounts = undefined;
        state.wkDomainAccountCount = 0;

        state.domainRandomizableAccounts = undefined;
        state.domainRandomizableAccountCount = 0;
        state.domainRandomizableAccountType = undefined;
    },
};
